import Call from "@/script/call";

const CH_CHECK = "douDianBatchOperationProduct.checkPromotionValidation";
const CH_CREATE = "douDianBatchOperationProduct.unionAllowanceCreateApply";
/** 创建前重新校验时的单批商品数 */
const CHECK_BATCH_SIZE = 50;

function toSec(ms) {
    return Math.floor(Number(ms) / 1000);
}

function chunkArr(arr, size) {
    const out = [];
    for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
    return out;
}

/**
 * 计算商品行优惠金额（单位分，整数）。
 * 优先行级覆盖值（avg_credit/max_credit），否则按表单统一设置换算；
 * 折扣模式一律按商品 min_price（最小金额）计算：credit = round(min_price × (1 − 折扣/10))。
 * 浮动面额开启时返回 maxCredit，且保证 maxCredit > credit。
 */
export function xrRowCredits(row, form) {
    if (row && row.avg_credit != null && row.avg_credit !== "") {
        const credit = Math.max(0, Math.round(Number(row.avg_credit)));
        let maxCredit = null;
        if (form.float_on) {
            maxCredit =
                row.max_credit != null && row.max_credit !== ""
                    ? Math.max(0, Math.round(Number(row.max_credit)))
                    : credit + 1;
            if (maxCredit <= credit) maxCredit = credit + 1;
        }
        return { credit, maxCredit };
    }
    if (form.promo_mode === "discount") {
        const price = Math.round(Number(row && row.min_price) || 0);
        if (form.float_on) {
            const credit = Math.max(0, Math.round(price * (1 - Number(form.avg_discount) / 10)));
            let maxCredit = Math.max(0, Math.round(price * (1 - Number(form.max_discount) / 10)));
            if (maxCredit <= credit) maxCredit = credit + 1;
            return { credit, maxCredit };
        }
        return {
            credit: Math.max(0, Math.round(price * (1 - Number(form.discount) / 10))),
            maxCredit: null,
        };
    }
    if (form.float_on) {
        const credit = Math.max(0, Math.round(Number(form.avg_amount) * 100));
        let maxCredit = Math.max(0, Math.round(Number(form.max_amount) * 100));
        if (maxCredit <= credit) maxCredit = credit + 1;
        return { credit, maxCredit };
    }
    return { credit: Math.max(0, Math.round(Number(form.amount) * 100)), maxCredit: null };
}

/** 全店商品模式下店铺的优惠金额（分）：优先店铺行覆盖（元），否则取立减面额默认值 */
export function xrShopCredit(shopId, state) {
    const form = state.xr_form;
    const ov = state.xr_shop_check && state.xr_shop_check[String(shopId)];
    if (ov && ov.amount != null && ov.amount !== "") {
        return Math.max(0, Math.round(Number(ov.amount) * 100));
    }
    return Math.max(0, Math.round(Number(form.amount) * 100));
}

function failReasonOf(rs, fallback) {
    return (rs && rs.msg) || fallback || "创建失败";
}

/**
 * 创建新人礼金活动主流程。
 * 指定商品：按店铺分组 → 创建前按最终金额批量校验（失败商品跳过并标记）→ 按单活动商品个数切片创建。
 * 全店商品：逐店铺校验（participate_type=2）→ 通过后创建。
 * 活动名称由 node 层（thread/douDianBatchOperationProduct.js）按 title_mode/title_prefix + 开始时间拼接。
 */
export async function runXinrenlijinCreate({ state, commit, shopList }) {
    const form = state.xr_form;
    const range = state.xr_date_range || [];
    const startSec = toSec(range[0]);
    const endSec = toSec(range[1]);
    const stopRequested = () => state.xr_create_stop_requested;
    const shopOf = (id) =>
        (shopList || []).find((s) => String(s.id) === String(id)) || null;

    if (Number(form.participate_type) === 2) {
        await runShopMode({ state, commit, shopOf, startSec, endSec, stopRequested });
        return;
    }
    await runProductMode({ state, commit, shopOf, startSec, endSec, stopRequested });
}

async function runShopMode({ state, commit, shopOf, startSec, endSec, stopRequested }) {
    const form = state.xr_form;
    const shops = (state.xr_selected_shop_ids || [])
        .map((id) => shopOf(id))
        .filter(Boolean);
    commit("xrCreateStart", shops.length);
    for (const shop of shops) {
        if (stopRequested()) break;
        const shopName = shop.shop_name || shop.id;
        const credit = xrShopCredit(shop.id, state);
        const checkBody = {
            participate_type: 2,
            activity_tool_type: 4,
            activity_tool_discount_type: 1,
            validate_type: 2,
            credit,
            start_time: startSec,
            end_time: endSec,
        };
        const ck = await Call.invoke(CH_CHECK, shop, checkBody);
        const info = ck && ck.data && ck.data.activity_check_info;
        if (!ck || ck.code != 1) {
            commit("xrCreateResultPush", [
                { ok: false, shop_name: shopName, product_id: "-", title: "全店商品", message: failReasonOf(ck, "校验请求失败") },
            ]);
            commit("xrCreateProgress", { done: 1, fail: 1 });
            commit("setXrShopCheckOne", { shopId: shop.id, patch: { pass: false, reason: failReasonOf(ck, "校验请求失败") } });
            continue;
        }
        if (info && info.check_pass === false) {
            const reason = info.simplified_reject_reason || info.reject_reason || "店铺校验不通过";
            commit("xrCreateResultPush", [
                { ok: false, shop_name: shopName, product_id: "-", title: "全店商品", message: reason },
            ]);
            commit("xrCreateProgress", { done: 1, fail: 1 });
            commit("setXrShopCheckOne", { shopId: shop.id, patch: { pass: false, reason } });
            continue;
        }
        commit("setXrShopCheckOne", { shopId: shop.id, patch: { pass: true, reason: "" } });
        const body = {
            activity_id: "",
            activity_start_time: startSec,
            activity_end_time: endSec,
            create_source: "marketing_tool_page_create",
            participate_type: 2,
            shop_deduction: { shop_deduction_credit: credit },
            full_shop_black_products: [],
            renew_switch_on: false,
        };
        const rs = await Call.invoke(CH_CREATE, shop, body, form.title_mode, form.title_prefix);
        const ok = rs && rs.code == 1;
        const applyId = ok && rs.data && rs.data.apply_id ? String(rs.data.apply_id) : "";
        commit("xrCreateResultPush", [
            {
                ok,
                shop_name: shopName,
                product_id: applyId || "-",
                title: "全店商品",
                message: ok ? `创建成功${applyId ? `，活动ID：${applyId}` : ""}` : failReasonOf(rs),
            },
        ]);
        commit("xrCreateProgress", { done: 1, success: ok ? 1 : 0, fail: ok ? 0 : 1 });
    }
}

async function runProductMode({ state, commit, shopOf, startSec, endSec, stopRequested }) {
    const form = state.xr_form;
    const selKeys = new Set(state.xr_product_selects || []);
    const rows = (state.xr_products || []).filter((r) => selKeys.has(r._key));
    if (!rows.length) return;
    const byShop = new Map();
    for (const r of rows) {
        const k = String(r.shop_id);
        if (!byShop.has(k)) byShop.set(k, []);
        byShop.get(k).push(r);
    }
    commit("xrCreateStart", rows.length);
    // 重置待创建状态
    for (const r of rows) {
        commit("xrPatchProduct", { _key: r._key, patch: { create_status: 0, create_msg: "", apply_id: "" } });
    }
    const perActivity = Math.min(500, Math.max(1, Number(form.per_activity_num) || 50));

    for (const [shopId, list] of byShop) {
        if (stopRequested()) break;
        const shop = shopOf(shopId) || list[0].shop_full || null;
        if (!shop) {
            for (const r of list) {
                commit("xrPatchProduct", { _key: r._key, patch: { create_status: 2, create_msg: "店铺信息缺失" } });
                commit("xrCreateProgress", { done: 1, fail: 1 });
            }
            continue;
        }
        // 创建前按最终金额重新批量校验，失败商品标记并跳过
        for (const ckChunk of chunkArr(list, CHECK_BATCH_SIZE)) {
            if (stopRequested()) break;
            const checkBody = {
                participate_type: 1,
                activity_tool_type: 4,
                activity_tool_discount_type: 1,
                validate_type: 2,
                product_infos: ckChunk.map((r) => ({
                    product_id: String(r.product_id),
                    credit: xrRowCredits(r, form).credit,
                })),
                start_time: startSec,
                end_time: endSec,
            };
            const ck = await Call.invoke(CH_CHECK, shop, checkBody);
            if (!ck || ck.code != 1) {
                const msg = failReasonOf(ck, "校验请求失败");
                for (const r of ckChunk) {
                    commit("xrPatchProduct", { _key: r._key, patch: { create_status: 2, create_msg: msg } });
                    commit("xrCreateResultPush", [
                        { ok: false, shop_name: shop.shop_name || shopId, product_id: String(r.product_id), title: r.name || "", message: msg },
                    ]);
                    commit("xrCreateProgress", { done: 1, fail: 1 });
                }
                continue;
            }
            const actInfo = ck.data && ck.data.activity_check_info;
            const actReject = actInfo && actInfo.check_pass === false
                ? actInfo.simplified_reject_reason || actInfo.reject_reason || "活动校验不通过"
                : "";
            const prodInfos = (ck.data && ck.data.product_check_infos) || {};
            for (const r of ckChunk) {
                const pi = prodInfos[String(r.product_id)];
                let reason = "";
                if (actReject) reason = actReject;
                else if (pi && pi.check_pass === false) {
                    reason = pi.simplified_reject_reason || pi.reject_reason || "商品校验不通过";
                }
                if (reason) {
                    commit("xrPatchProduct", { _key: r._key, patch: { create_status: 2, create_msg: reason } });
                    commit("xrCreateResultPush", [
                        { ok: false, shop_name: shop.shop_name || shopId, product_id: String(r.product_id), title: r.name || "", message: reason },
                    ]);
                    commit("xrCreateProgress", { done: 1, fail: 1 });
                }
            }
        }
        // 校验通过的商品按单活动商品个数切片创建
        const valid = list.filter((r) => r.create_status === 0);
        for (const chunk of chunkArr(valid, perActivity)) {
            if (stopRequested()) break;
            for (const r of chunk) {
                commit("xrPatchProduct", { _key: r._key, patch: { create_status: 3, create_msg: "" } });
            }
            const body = {
                activity_id: "",
                product_deductions: chunk.map((r) => {
                    const { credit, maxCredit } = xrRowCredits(r, form);
                    const item = {
                        name: r.name || "",
                        img: r.cover || "",
                        product_id: String(r.product_id),
                        shop_deduction_credit: credit,
                    };
                    if (form.float_on && maxCredit != null) {
                        item.max_shop_deduction_credit = maxCredit;
                    }
                    return item;
                }),
                activity_start_time: startSec,
                activity_end_time: endSec,
                deduction_discount_type: form.float_on ? 1 : 0,
                create_source: "marketing_tool_page_create",
                participate_type: 1,
                full_shop_black_products: [],
                renew_switch_on: false,
            };
            const rs = await Call.invoke(CH_CREATE, shop, body, form.title_mode, form.title_prefix);
            const ok = rs && rs.code == 1;
            const applyId = ok && rs.data && rs.data.apply_id ? String(rs.data.apply_id) : "";
            const msg = ok ? "" : failReasonOf(rs);
            const resultRows = [];
            for (const r of chunk) {
                commit("xrPatchProduct", {
                    _key: r._key,
                    patch: {
                        create_status: ok ? 1 : 2,
                        create_msg: ok ? "创建成功" : msg,
                        apply_id: applyId,
                    },
                });
                resultRows.push({
                    ok,
                    shop_name: shop.shop_name || shopId,
                    product_id: String(r.product_id),
                    title: r.name || "",
                    message: ok ? `创建成功${applyId ? `，活动ID：${applyId}` : ""}` : msg,
                });
            }
            commit("xrCreateResultPush", resultRows);
            commit("xrCreateProgress", {
                done: chunk.length,
                success: ok ? chunk.length : 0,
                fail: ok ? 0 : chunk.length,
            });
        }
    }
}
